<script setup>
import { computed, reactive, ref, nextTick } from 'vue';
import { useVuelidate } from '@vuelidate/core';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { useMapGetter } from 'dashboard/composables/store';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import TagInput from 'dashboard/components-next/taginput/TagInput.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import FormInput from 'v3/components/Form/Input.vue';
import validations from './helpers/validations';

const emit = defineEmits(['close']);

const dialogRef = ref(null);
const store = useStore();
const { t } = useI18n();

const state = reactive({
  title: '',
  description: '',
});

const rules = {
  title: validations.title,
  description: validations.description,
};
const v$ = useVuelidate(rules, state);

const selectedAgents = ref([]);
const submitInProgress = ref(false);

const agentList = useMapGetter('agents/getAgents');
const teamsList = useMapGetter('teams/getTeams');

const assignedAgentIds = computed(() => {
  const ids = new Set();

  teamsList.value.forEach(team => {
    const members = store.getters['teamMembers/getTeamMembers'](team.id);
    members.forEach(member => ids.add(member.id));
  });

  return ids;
});

const selectedAgentNames = computed(() =>
  selectedAgents.value.map(
    id => agentList.value.find(a => a.id === id)?.name ?? ''
  )
);

const agentMenuItems = computed(() =>
  agentList.value
    .filter(
      ({ id }) =>
        !selectedAgents.value.includes(id) && !assignedAgentIds.value.has(id)
    )
    .map(({ id, name, thumbnail, avatar_url }) => ({
      label: name,
      value: id,
      action: 'select',
      thumbnail: { name, src: thumbnail || avatar_url || '' },
    }))
);

function handleAgentAdd({ value }) {
  if (!selectedAgents.value.includes(value)) {
    selectedAgents.value.push(value);
  }
}

function handleAgentRemove(index) {
  selectedAgents.value.splice(index, 1);
}

function resetState() {
  state.title = '';
  state.description = '';
  selectedAgents.value = [];
  nextTick(() => v$.value.$reset());
}

async function loadAssignableAgents() {
  await Promise.all([
    store.dispatch('agents/get'),
    store.dispatch('teams/get'),
  ]);
  await Promise.all(
    teamsList.value.map(team =>
      store.dispatch('teamMembers/get', { teamId: team.id })
    )
  );
}

async function open() {
  resetState();
  dialogRef.value?.open();
  try {
    await loadAssignableAgents();
  } catch {
    useAlert(t('TEAMS_SETTINGS.TEAM_FORM.ERROR_MESSAGE'));
  }
}

function close() {
  dialogRef.value?.close();
}

defineExpose({ open, close });

function suppressEnterSubmit(e) {
  if (e.target?.tagName === 'TEXTAREA') return;
  e.preventDefault();
}

async function handleSave() {
  v$.value.$touch();
  if (v$.value.$invalid) return;

  submitInProgress.value = true;
  try {
    const team = await store.dispatch('teams/create', {
      name: state.title,
      description: state.description,
      allow_auto_assign: true,
    });

    if (selectedAgents.value.length) {
      await store.dispatch('teamMembers/create', {
        teamId: team.id,
        agentsList: selectedAgents.value,
      });
    }

    await store.dispatch('teams/get');
    close();
    emit('close');
  } catch (error) {
    useAlert(error?.message || t('TEAMS_SETTINGS.TEAM_FORM.ERROR_MESSAGE'));
  } finally {
    submitInProgress.value = false;
  }
}

function handleCancel() {
  close();
}

function handleDialogClose() {
  resetState();
  emit('close');
}
</script>

<template>
  <Dialog
    ref="dialogRef"
    type="edit"
    :title="t('TEAMS_SETTINGS.MODAL.TITLE')"
    :description="t('TEAMS_SETTINGS.MODAL.DESCRIPTION')"
    :show-cancel-button="false"
    :show-confirm-button="false"
    overflow-y-auto
    width="xl"
    @close="handleDialogClose"
  >
    <div class="grid w-full gap-5" @keydown.enter.exact="suppressEnterSubmit">
      <FormInput
        v-model="state.title"
        name="team-create-title"
        spacing="compact"
        :label="t('TEAMS_SETTINGS.MODAL.NAME_LABEL')"
        :placeholder="t('TEAMS_SETTINGS.MODAL.NAME_PLACEHOLDER')"
        :has-error="v$.title.$error"
        :error-message="v$.title.$error ? v$.title.$errors[0].$message : ''"
        @blur="v$.title.$touch"
      />

      <TextArea
        id="team-create-description"
        v-model="state.description"
        :label="t('TEAMS_SETTINGS.MODAL.DESCRIPTION_LABEL')"
        :placeholder="t('TEAMS_SETTINGS.MODAL.DESCRIPTION_PLACEHOLDER')"
        :resize="false"
        custom-text-area-class="min-h-[5rem]"
        @blur="v$.description.$touch"
      />

      <div>
        <p class="mb-2 text-sm font-medium text-n-slate-12">
          {{ t('TEAMS_SETTINGS.MODAL.MEMBERS_LABEL') }}
        </p>
        <div
          class="flex items-start gap-2 rounded-xl bg-n-alpha-black2 px-2 py-2 outline outline-1 -outline-offset-1 outline-n-weak hover:outline-n-strong"
        >
          <div class="min-w-0 flex-1">
            <TagInput
              :model-value="selectedAgentNames"
              :placeholder="t('TEAMS_SETTINGS.MODAL.MEMBERS_PLACEHOLDER')"
              :menu-items="agentMenuItems"
              show-dropdown
              skip-label-dedup
              :auto-open-dropdown="false"
              @add="handleAgentAdd"
              @remove="handleAgentRemove"
            />
          </div>
          <span
            class="mt-2 size-5 shrink-0 text-n-slate-10 i-lucide-chevron-down"
            aria-hidden="true"
          />
        </div>
      </div>
    </div>

    <template #footer>
      <div class="flex w-full items-center justify-end gap-3">
        <Button
          variant="faded"
          color="slate"
          type="button"
          :label="t('DIALOG.BUTTONS.CANCEL')"
          @click="handleCancel"
        />
        <Button
          teal
          type="button"
          :label="t('TEAMS_SETTINGS.MODAL.SAVE')"
          :disabled="v$.title.$invalid"
          :is-loading="submitInProgress"
          @click="handleSave"
        />
      </div>
    </template>
  </Dialog>
</template>
